<template>
    <Card
        icon="fas fa-calendar-alt"
        bg-color="tw:bg-blue-600"
        title-color="white"
        title-classes="tw:border-b tw:border-gray-200"
        :title="'Fechas e imagen'"
        padding="0"
    >
        <div class="tw:p-6 tw:space-y-4">
            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                <div>
                    <BaseDateTimeInput
                        :label="'Fecha de inicio'"
                        name="start_event_date"
                        v-model="start_event_dateModel"
                        :min="minDate"
                        rules="required"
                    />
                    <small class="tw:text-gray-500 tw:tracking-wide tw:mt-1 tw:text-end tw:text-[11px] tw:block">{{ '(COMIENZO DEL EVENTO)' }}</small>
                </div>
                <div>
                    <BaseDateTimeInput
                        :label="'Fecha de fin'"
                        name="end_event_date"
                        v-model="end_event_dateModel"
                        :min="localData.start_event_date"
                        :disabled="!localData.start_event_date"
                        rules="required"
                    />
                    <small class="tw:text-gray-500 tw:tracking-wide tw:mt-1 tw:text-end tw:text-[11px] tw:block">{{ '(FINAL DEL EVENTO)' }}</small>
                </div>
            </div>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div>
                <label class="tw:block tw:text-gray-500 tw:mb-2">
                    {{ 'Imagen del evento' }}
                    <b class="tw:text-xs">{{ '(MEDIDAS: 1020X340 - FORMATO: JPG - PESO MÁXIMO: Menos de 500 KB)' }}</b>
                </label>
                <FileDropzone
                    v-if="url"
                    :url="url"
                    :height="340"
                    :width="1020"
                    name="image"
                    :rules="imageRequired ? 'required' : ''"
                    :preview="localData.image"
                    @success="(file, response) => emit('imagen-subida', file, response)"
                    @removed="emit('imagen-borrada')"
                />
            </div>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                <BaseInputText
                    id="meta_title"
                    name="meta_title"
                    :label="'Meta título'"
                    :value="localData.meta_title"
                    rules="max:80"
                    :placeholder="'Meta título'"
                    :maxlength="80"
                    @input="val => { localData.meta_title = val; markMetaTitleAsUserModified(); }"
                />
                <BaseInputText
                    id="meta_description"
                    name="meta_description"
                    :label="'Meta descripción'"
                    :value="localData.meta_description"
                    rules="max:160"
                    :placeholder="'Meta descripción'"
                    :maxlength="160"
                    @input="val => localData.meta_description = val"
                />
            </div>

            <div>
                <BaseInputText
                    id="youtube"
                    name="youtube"
                    :label="'Id del video en Youtube'"
                    :value="localData.youtube_id"
                    :placeholder="'Id del video en Youtube'"
                    @input="val => localData.youtube_id = val"
                />
            </div>
        </div>
    </Card>
</template>

<script setup>
    import { computed, inject, ref, set } from 'vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import FileDropzone from '@/modules/admin/ui/FileDropzone.vue';
    import BaseDateTimeInput from '@/modules/admin/ui/BaseDateTimeInput.vue';

    const localData = inject('eventoFormData');
    const markMetaTitleAsUserModified = inject('markMetaTitleAsUserModified');

    // `imageRequired` comes from useEventForm (via FormEvento's provide): the
    // image is required only for non-draft events (published/cancelled/
    // postponed) and stays optional for drafts, so a manager can create the
    // event first and upload the artwork later. The sports trio exemption is
    // folded into that predicate already. Defaulted to `true` so the component
    // still demands the image if it is ever rendered outside FormEvento.
    const { imageRequired = ref(true) } = inject('eventoFormValidation', {});

    const props = defineProps({
        url: { type: String, default: '' }
    });

    const emit = defineEmits(['imagen-subida', 'imagen-borrada', 'clear-fecha']);

    const timezone = computed(() => {
        try {
            return window?.config?.TIMEZONE || Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
    });

    function clearFecha() {
        emit('clear-fecha');
    }

    // minDate is the current moment in local time, formatted as
    // "YYYY-MM-DDTHH:mm" (what <input type="datetime-local"> expects).
    // The picker rejects values earlier than this, preventing events
    // with a start date in the past. Computed once at mount; if the
    // form is left open for hours the min won't tick forward — minor UX
    // trade-off vs. adding a setInterval.
    const minDate = computed(() => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const mi = String(now.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    });

    const start_event_dateModel = computed({
        get: () => localData.start_event_date,
        set: val => {
            // Use set() instead of direct assignment to ensure reactivity
            // for properties that didn't exist when reactive() was called.
            set(localData, 'start_event_date', val);
            clearFecha();
        }
    });

    const end_event_dateModel = computed({
        get: () => localData.end_event_date,
        set: val => {
            set(localData, 'end_event_date', val);
        }
    });

    // Validation lives inside FileDropzone now (it owns its own
    // ValidationProvider). The parent just wires emits to localData so
    // the useEventForm imagenSubida / imagenBorrada handlers run on
    // upload/remove — see FileDropzone.vue for the validation details.
</script>
